import { useState, useEffect } from 'react';
import { transactionsService, merchantsService, tradersService, dictionariesService } from '../../../services';
import { Transaction, TransactionFilters } from '../../../types/transaction';
import { Trader } from '../../../types/trader';
import { PaymentSystem } from '../../../types/common/payment';
import { useNotification } from '../../../contexts/NotificationContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { canPerformAction, ActionKey } from '../../../auth/actionMatrix';

export function useTransactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [merchants, setMerchants] = useState<{ id: string; name: string | null }[]>([]);
  const [traders, setTraders] = useState<Trader[]>([]);
  const [paymentSystems, setPaymentSystems] = useState<PaymentSystem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<TransactionFilters>({
    merchant_id: undefined,
    trader_id: undefined,
    payment_system_id: undefined,
    status: undefined,
    start_date: undefined,
    end_date: undefined,
    search_id: undefined,
    include_not_found: false,
    is_sandbox: undefined,
    page: 1,
    per_page: 20
  });
  const { showNotification } = useNotification();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Загрузка справочников при монтировании компонента
  useEffect(() => {
    const loadDictionaries = async () => {
      try {
        const [merchantsData, tradersData, paymentSystemsData] = await Promise.all([
          merchantsService.getTransactionOptions(),
          tradersService.getTraders({ is_archive: false }),
          dictionariesService.getPaymentSystems(),
        ]);
        setMerchants(merchantsData);
        setTraders(tradersData);
        setPaymentSystems(paymentSystemsData);
      } catch (err) {
        showNotification('Ошибка при загрузке справочников', 'error');
      }
    };
    loadDictionaries();
  }, []);

  const buildRequestParams = (activeFilters: TransactionFilters) => {
    const requestParams: Record<string, string | number | boolean> = {
      page: activeFilters.page ?? 1,
      per_page: activeFilters.per_page ?? 20,
      include_not_found: activeFilters.include_not_found ?? false,
    };

    if (activeFilters.merchant_id) requestParams.merchant_id = activeFilters.merchant_id;
    if (activeFilters.trader_id) requestParams.trader_id = activeFilters.trader_id;
    if (activeFilters.payment_system_id) requestParams.payment_system_id = activeFilters.payment_system_id;
    if (activeFilters.currency) requestParams.currency = activeFilters.currency;
    if (activeFilters.status) requestParams.status = activeFilters.status;
    if (activeFilters.start_date) requestParams.start_date = activeFilters.start_date;
    if (activeFilters.end_date) requestParams.end_date = activeFilters.end_date;
    if (activeFilters.search_id) requestParams.search_id = activeFilters.search_id;
    if (activeFilters.is_sandbox !== undefined) requestParams.is_sandbox = activeFilters.is_sandbox;

    return requestParams;
  };

  const loadData = async (customFilters?: TransactionFilters) => {
    setLoading(true);
    try {
      const activeFilters = customFilters ?? filters;
      const requestParams = buildRequestParams(activeFilters);

      const transactionsData = await transactionsService.getTransactions(requestParams);
      setTransactions(transactionsData.items);
      setTotalPages(transactionsData.pages);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке данных');
      showNotification('Ошибка при загрузке данных', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleFilterChange = (field: keyof TransactionFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'currency' && value && prev.payment_system_id && !paymentSystems.some((system) => {
        const currency = system.currency;
        const code = typeof currency === 'string' ? currency : currency?.code || currency?.iso_code;
        return system.id === prev.payment_system_id && code === value;
      }) ? { payment_system_id: undefined } : {}),
      page: field === 'page' ? value : 1
    }));
  };

  const handleSearch = () => {
    loadData();
  };

  const handleReset = () => {
    const resetFilters = {
      merchant_id: undefined,
      trader_id: undefined,
      payment_system_id: undefined,
      status: undefined,
      start_date: undefined,
      end_date: undefined,
      search_id: undefined,
      include_not_found: false,
      is_sandbox: undefined,
      page: 1,
      per_page: 20
    };
    setFilters(resetFilters);
    loadData(resetFilters);
  };

  const handleToggleIncludeNotFound = () => {
    setFilters((prev) => {
      const newFilters: TransactionFilters = {
        ...prev,
        include_not_found: !prev.include_not_found,
        page: 1,
      };
      loadData(newFilters);
      return newFilters;
    });
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        page: value
      };
      loadData(newFilters);
      return newFilters;
    });
  };

  const handleViewDetails = async (transaction: Transaction) => {
    if (!canPerformAction(currentUser, ActionKey.TRANSACTIONS_VIEW_DETAILS)) {
      showNotification('У вас нет прав для просмотра деталей транзакций', 'error');
      return;
    }
    setSelectedTransaction(transaction);
    setDetailsDialogOpen(true);
    try {
      const transactionDetails = await transactionsService.getTransaction(transaction.id);
      setSelectedTransaction(transactionDetails);
    } catch (err) {
      showNotification('Не удалось загрузить актуальные детали транзакции', 'error');
    }
  };

  const handleRefresh = () => {
    loadData(filters);
  };

  return {
    transactions,
    merchants,
    traders,
    paymentSystems,
    error,
    loading,
    selectedTransaction,
    detailsDialogOpen,
    totalPages,
    filters,
    setDetailsDialogOpen,
    setSelectedTransaction,
    handleFilterChange,
    handleSearch,
    handleReset,
    handlePageChange,
    handleViewDetails,
    handleRefresh,
    handleToggleIncludeNotFound,
    loadData,
  };
}
